import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u3_x1_2punkt';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einleitung',
    body: '2-Punkt-Ventilantriebe sind eine grundlegende Komponente in der Heizungs-, Lüftungs- und Klimatechnik (HLK). Sie ermöglichen das einfache Öffnen und Schließen von Ventilen und werden für vielfältige Absperr- und Regelungsaufgaben eingesetzt.'
  },
  {
    heading: 'Was bedeutet "2-Punkt-Antrieb"?',
    body: 'Der Begriff "2-Punkt" bezieht sich darauf, dass der Antrieb und das damit verbundene Ventil nur zwei definierte Betriebszustände einnehmen können:\n\n•   Position 1: AUF (Ventil vollständig geöffnet)\n•   Position 2: ZU (Ventil vollständig geschlossen)\n\nEs gibt keine Zwischenstellungen für eine stetige oder proportionale Regelung des Durchflusses. Die Ansteuerung erfolgt durch ein einfaches Ein-/Aus-Signal. Liegt ein Steuersignal an (z.B. Spannung), bewegt sich der Antrieb in die eine Endlage. Wird das Signal entfernt, kehrt der Antrieb in die andere Endlage zurück, oft mittels Federkraft oder einer Umkehr der Motorbewegung.'
  },
  {
    heading: 'Thermische Antriebe',
    body: 'Funktion: Ein Dehnstoffelement (oft Wachs) erwärmt sich bei Spannung (230V AC oder 24V AC/DC) und dehnt sich aus, wodurch das Ventil betätigt wird. Ohne Spannung kühlt es ab und das Ventil kehrt in die Ruhestellung zurück (meist stromlos geschlossen - NC).\n\nVorteile: Preisgünstig, leise, kompakt.\nNachteile: Langsame Stellzeit (mehrere Minuten).\n\nAnschluss: Typischerweise 2-adrig (Spannung AN/AUS).'
  },
  {
    heading: 'Motorische Antriebe',
    body: 'Funktion: Ein Elektromotor bewegt das Ventil über ein Getriebe. Bei 2-Punkt-Antrieben wird oft eine Feder für die Rückstellung in die Ruhelage verwendet (Federrücklauf), wenn die Spannung abgeschaltet wird.\n\nVorteile: Schnellere Stellzeit, höhere Stellkräfte.\nNachteile: Teurer, mögliche Laufgeräusche.\n\nAnschluss: Oft 2- oder 3-adrig, je nach Ausführung (z.B. L, N für Versorgung; Y für Steuersignal AUF).'
  },
  {
    heading: 'NC/NO und typische Einsatzgebiete',
    body: '•   NC (Normally Closed - Stromlos Geschlossen): Das Ventil ist ZU, wenn keine Spannung am Antrieb anliegt. Es ÖFFNET bei Spannung. Typisch für Heizkörper oder Zonen, um bei Stromausfall ein Überhitzen zu verhindern.\n•   NO (Normally Open - Stromlos Offen): Das Ventil ist AUF, wenn keine Spannung anliegt. Es SCHLIESST bei Spannung. Seltener, z.B. für Bypass-Anwendungen.\n\nEinsatzgebiete:\n•   Zonenregelung (Heizkreise absperren)\n•   Einzelraumregelung an Heizkörpern\n•   Umschaltventile (Brauchwasser-Vorrang, Heiz-/Kühlbetrieb)\n•   Lüftungsanlagen (Absperrklappen)\n•   Solaranlagen und industrielle Prozesse'
  },
  {
    heading: 'Fazit',
    body: '2-Punkt-Antriebe sind eine zuverlässige und kostengünstige Lösung für viele AUF/ZU-Anwendungen in der Gebäudetechnik.'
  }
];

const images = [
  { id: 'img_va2p_beispiel', label: 'Ventilantriebe Beispiel', src: `${IMAGE_BASE}ventil_moehlenhof.webp`, alt: 'Beispiele für 2-Punkt Ventilantriebe' },
  { id: 'img_va2p_2wege', label: '2-Wege Ventil', src: `${IMAGE_BASE}ventil_2wege.PNG`, alt: '2-Wege Ventil Darstellung' },
  { id: 'img_va2p_schnitt', label: 'Ventil Schnitt', src: `${IMAGE_BASE}ventil_schnitt.webp`, alt: 'Thermischer Ventilantrieb Schnitt' },
  { id: 'img_va2p_schaltplan_therm', label: 'Schaltplan thermisch', src: `${IMAGE_BASE}schaltplan_2punkt_thermisch.PNG`, alt: 'Schaltplan thermischer 2-Punkt-Antrieb' },
  { id: 'img_va2p_schaltplan_motor', label: 'Schaltplan motorisch', src: `${IMAGE_BASE}schaltplan_2punkt_motorisch.PNG`, alt: 'Schaltplan motorischer 2-Punkt-Antrieb' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.3.1 2-Punkt-Ventilantriebe', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u3_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


